import React from 'react'
import type { LucideIcon } from 'lucide-react'

export type EmptyStateVariant = 'info' | 'warning' | 'success' | 'neutral'

export interface EmptyStateAction {
  label: string
  onClick: () => void
  icon?: LucideIcon
}

export interface EmptyStateProps {
  icon: LucideIcon
  title: string
  description: string
  action?: EmptyStateAction
  variant?: EmptyStateVariant
  compact?: boolean
}

const variantStyles = {
  info: {
    bg: 'from-brand-primary/20 to-brand-accent-600/20',
    icon: 'text-brand-primary'
  },
  warning: {
    bg: 'from-yellow-500/20 to-orange-500/20',
    icon: 'text-yellow-500'
  },
  success: {
    bg: 'from-green-500/20 to-emerald-500/20',
    icon: 'text-green-500'
  },
  neutral: {
    bg: 'from-brand-gray/20 to-brand-gray-light/20',
    icon: 'text-brand-gray'
  }
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  variant = 'neutral',
  compact = false
}: EmptyStateProps) {
  const styles = variantStyles[variant]
  const minHeight = compact ? 'min-h-[150px]' : 'min-h-[200px]'

  return (
    <div className={`flex flex-col items-center justify-center ${minHeight} p-6 sm:p-8`}>
      {/* Icon */}
      <div className={`w-12 h-12 ${compact ? 'sm:w-14 sm:h-14' : 'sm:w-16 sm:h-16'} bg-gradient-to-br ${styles.bg} rounded-full flex items-center justify-center mb-3 sm:mb-4 shadow-monumenta`}>
        <Icon className={`w-6 h-6 ${compact ? 'sm:w-7 sm:h-7' : 'sm:w-8 sm:h-8'} ${styles.icon}`} />
      </div>

      {/* Title */}
      <h3 className={`font-display ${compact ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'} font-semibold text-white mb-2 text-center`}>
        {title}
      </h3>

      {/* Description */}
      <p className={`font-body ${compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'} text-brand-gray text-center max-w-md mb-4`}>
        {description}
      </p>

      {/* Optional Action Button */}
      {action && (
        <button
          onClick={action.onClick}
          className="btn-primary flex items-center space-x-2 text-sm"
        >
          {action.icon && <action.icon className="w-4 h-4" />}
          <span>{action.label}</span>
        </button>
      )}
    </div>
  )
}
